import type { AssistantSurface } from 'src/types/assistant';

/**
 * Back to the assistant (app#397, spec §3 p. 5, §4 p. 6). A person who leaves the assistant's window
 * for a car's page — a card, "Book these dates", "Prepare the offer", the dealer — and presses Back
 * finds the catalog with the window open again, the conversation where it was.
 *
 * The catalog's entry of the history is marked just before the person leaves it, and the catalog
 * reads the mark when it is back on screen: the window opens once, and the mark goes. Only that
 * entry is marked, so a second Back leads on as usual, and a catalog reached any other way — a tab,
 * a link, a reload of its own address — opens without the window. The mark sits in the entry's own
 * state, beside the router's keys, so it is there whether Back is the app's (popstate) or the
 * browser's with a document of its own (a reload on the car's page, then Back).
 */

const MARK = 'gacAssistant';

type HistoryState = Record<string, unknown> | null;

const currentState = (): HistoryState => {
  const state: unknown = window.history.state;

  return typeof state === 'object' && state !== null ? (state as Record<string, unknown>) : null;
};

/** Marks the entry on screen as the one the person left the `surface` catalog's assistant from. */
export const markReturnToAssistant = (surface: AssistantSurface): void => {
  try {
    window.history.replaceState({ ...currentState(), [MARK]: surface }, '');
  } catch {
    // A history that refuses the state: Back still works, only without the window.
  }
};

/** Whether this entry was left from the `surface` assistant — answered once: the mark is taken. */
export const takeReturnToAssistant = (surface: AssistantSurface): boolean => {
  const state = currentState();

  if (!state || state[MARK] !== surface) return false;

  const rest = { ...state };
  delete rest[MARK];

  try {
    window.history.replaceState(rest, '');
  } catch {
    // The mark stays: the window would open on the next Back to this entry too, which is harmless.
  }

  return true;
};
